import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ErrorResponse } from '../api';
import { convertPreferencesToNotificationsSettings } from '../helpers/notificationHelpers';
import {
  fetchAvatars,
  fetchCarepoints,
  fetchCarepointsRewards,
  fetchJourneyRelationships,
  fetchMember,
  fetchMembership,
  fetchMyPreferences,
  fetchMyPreferredTopics,
  fetchReferralLink,
  fetchUser,
  refreshUserAfterProfileOnboarding,
  followAuthor,
  getAuthorInfo,
  getBlockedMembers,
  getMemberInfo,
  sendEmailsInvite,
  unfollowAuthor,
  updateMember,
  updateMemberbership,
  updateMyPreferences,
  updateMyPreferredTopics,
  updateUser,
  verifyEmail,
} from './actions';
import {
  Avatar,
  Carepoints,
  EmailVerficationErrors,
  Member,
  MemberInfo,
  Membership,
  NotificationSettings,
  PreferenceType,
  ProfileInformation,
  User,
} from './model';
import { CarepointsReward } from './model/CarepointsReward';
import { PreferenceValue } from './model/PreferenceValue';

export interface ProfileState {
  member?: Member;
  membership: Membership[];
  user?: User;
  carepoints?: Carepoints[];
  carepointsRewards?: CarepointsReward[];
  avatars: Avatar[];
  memberInfo?: MemberInfo;
  authorInfo?: MemberInfo;
  loadingAuthorInfo: boolean;
  myPreferredTopics?: PreferenceValue[];
  isUpdatingMember: boolean;
  isUpdatingUser: boolean;
  loadingFollowUnfollow: boolean;
  loadingPreferences: boolean;
  loadingUpdatePreferences: boolean;
  loadingAvatars: boolean;
  aboutYouData?: ProfileInformation;
  blockedMembers?: MemberInfo[];
  memberLoading?: boolean;
  memberError?: ErrorResponse;
  updateMemberError?: ErrorResponse;
  updateUserError?: ErrorResponse;
  referralLink?: string;
  loadingReferralLink: boolean;
  loadingSendingEmailsInvite: boolean;
  emailsSendingError?: ErrorResponse;
  myPreferredTopicsError?: ErrorResponse;
  myPreferences: NotificationSettings[];
  loadingMyPreferences: boolean;
  myPreferencesError?: ErrorResponse;
  isEmailVerificationSuccessful?: boolean;
  emailVerficationError?: EmailVerficationErrors;
  relationshipTypes: string[];
}

const initialState: ProfileState = {
  member: undefined,
  membership: [],
  user: undefined,
  carepoints: undefined,
  carepointsRewards: undefined,
  avatars: [],
  memberInfo: undefined,
  authorInfo: undefined,
  myPreferredTopics: undefined,
  loadingAuthorInfo: false,
  isUpdatingMember: false,
  isUpdatingUser: false,
  loadingFollowUnfollow: false,
  loadingPreferences: false,
  loadingUpdatePreferences: false,
  loadingAvatars: false,
  aboutYouData: undefined,
  blockedMembers: undefined,
  referralLink: undefined,
  loadingReferralLink: false,
  emailsSendingError: undefined,
  loadingSendingEmailsInvite: false,
  myPreferences: [],
  loadingMyPreferences: false,
  myPreferencesError: undefined,
  isEmailVerificationSuccessful: undefined,
  emailVerficationError: undefined,
  relationshipTypes: [],
};

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearMemberInfo: (state) => {
      state.memberInfo = undefined;
    },
    clearAuthorInfo: (state) => {
      state.authorInfo = undefined;
    },
    updateMemberInfo: (state, action: PayloadAction<string>) => {
      if (state.memberInfo?.memberId === action.payload) {
        state.memberInfo = { ...state.memberInfo, isCompanion: false };
      }
    },
    updateAddCompanionMemberInfo: (state, action: PayloadAction<string>) => {
      if (state.memberInfo?.memberId === action.payload) {
        state.memberInfo = { ...state.memberInfo, isCompanion: true };
      }
    },
    setAboutYouData: (state, action: PayloadAction<ProfileInformation>) => {
      state.aboutYouData = action.payload;
    },
    clearAboutYouData: (state) => {
      state.aboutYouData = undefined;
    },
    clearUpdateMemberError: (state) => {
      state.updateMemberError = undefined;
    },
    updateUserVerificationStatus: (
      state,
      action: PayloadAction<'verified' | 'pending' | 'unverified'>
    ) => {
      if (state.member && state.member.verification) {
        state.member.verification.verificationStatus = action.payload;
      }
    },
    clearMyPreferredTopics: (state, action: PayloadAction<void>) => {
      state.myPreferredTopics = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(refreshUserAfterProfileOnboarding.fulfilled, (state, action) => {
        if (state.member?.id === action.meta.arg) state.user = action.payload;
      })
      .addCase(fetchMember.pending, (state) => {
        state.memberLoading = true;
        state.memberError = undefined;
      })
      .addCase(fetchMember.fulfilled, (state, action) => {
        state.member = action.payload;
        state.memberLoading = false;
      })
      .addCase(fetchMember.rejected, (state, action) => {
        state.memberLoading = false;
        state.member = undefined;
        state.memberError = action.payload;
      })
      .addCase(fetchMembership.fulfilled, (state, action) => {
        state.membership = action.payload;
      })
      .addCase(updateUser.pending, (state) => {
        state.isUpdatingUser = true;
        state.updateUserError = undefined;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.isUpdatingUser = false;
        state.updateUserError = action.payload;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.isUpdatingUser = false;
        state.user = action.payload;
      })
      .addCase(updateMember.pending, (state) => {
        state.isUpdatingMember = true;
        state.updateMemberError = undefined;
      })
      .addCase(updateMember.rejected, (state, action) => {
        state.isUpdatingMember = false;
        state.updateMemberError = action.payload;
      })
      .addCase(updateMember.fulfilled, (state, action) => {
        state.isUpdatingMember = false;
        state.member = action.payload;
      })
      .addCase(updateMemberbership.fulfilled, (state, action) => {
        state.membership = action.payload;
      })
      .addCase(fetchCarepoints.fulfilled, (state, action) => {
        state.carepoints = action.payload ? action.payload : undefined;
      })
      .addCase(fetchCarepointsRewards.fulfilled, (state, action) => {
        state.carepointsRewards = action.payload;
      })
      .addCase(fetchAvatars.fulfilled, (state, action) => {
        state.avatars = action.payload;
        state.loadingAvatars = false;
      })
      .addCase(fetchAvatars.pending, (state) => {
        state.loadingAvatars = true;
      })
      .addCase(fetchAvatars.rejected, (state) => {
        state.loadingAvatars = false;
      })
      .addCase(getMemberInfo.fulfilled, (state, action) => {
        state.memberInfo = action.payload;
      })

      .addCase(getAuthorInfo.pending, (state) => {
        state.loadingAuthorInfo = true;
      })
      .addCase(getAuthorInfo.rejected, (state) => {
        state.loadingAuthorInfo = false;
        state.authorInfo = undefined;
      })
      .addCase(getAuthorInfo.fulfilled, (state, action) => {
        state.authorInfo = action.payload;
        state.loadingAuthorInfo = false;
      })

      .addCase(followAuthor.pending, (state) => {
        state.loadingFollowUnfollow = true;
      })
      .addCase(unfollowAuthor.pending, (state) => {
        state.loadingFollowUnfollow = true;
      })

      .addCase(followAuthor.rejected, (state) => {
        state.loadingFollowUnfollow = false;
      })
      .addCase(unfollowAuthor.rejected, (state) => {
        state.loadingFollowUnfollow = false;
      })
      .addCase(followAuthor.fulfilled, (state) => {
        if (state.authorInfo) {
          state.authorInfo.isFollowedByYou = true;
          state.authorInfo.followedByCount++;
        }
        state.loadingFollowUnfollow = false;
      })
      .addCase(unfollowAuthor.fulfilled, (state) => {
        if (state.authorInfo) {
          state.authorInfo.isFollowedByYou = false;
          if (state.authorInfo.followedByCount > 0) {
            state.authorInfo.followedByCount--;
          }
        }
        state.loadingFollowUnfollow = false;
      })

      .addCase(fetchMyPreferredTopics.pending, (state) => {
        state.loadingPreferences = true;
        state.myPreferredTopicsError = undefined;
      })
      .addCase(fetchMyPreferredTopics.rejected, (state, action) => {
        state.loadingPreferences = false;
        state.myPreferredTopics = [];
        state.myPreferredTopicsError = action.payload;
      })
      .addCase(fetchMyPreferredTopics.fulfilled, (state, action) => {
        state.loadingPreferences = false;
        if (action.payload && action.payload.values) {
          state.myPreferredTopics = action.payload.values;
        }
      })

      .addCase(updateMyPreferredTopics.pending, (state) => {
        state.loadingUpdatePreferences = true;
      })
      .addCase(updateMyPreferredTopics.rejected, (state) => {
        state.loadingUpdatePreferences = false;
        state.myPreferredTopics = [];
      })
      .addCase(updateMyPreferredTopics.fulfilled, (state, action) => {
        state.myPreferredTopics = action.payload.filter(
          (preference) => preference.type === PreferenceType.topics
        )[0].values;
        state.loadingUpdatePreferences = false;
      })
      .addCase(getBlockedMembers.fulfilled, (state, action) => {
        state.blockedMembers = action.payload ? action.payload : undefined;
      })
      .addCase(getBlockedMembers.rejected, (state) => {
        state.blockedMembers = undefined;
      })

      .addCase(fetchReferralLink.pending, (state) => {
        state.loadingReferralLink = true;
      })
      .addCase(fetchReferralLink.rejected, (state) => {
        state.referralLink = undefined;
        state.loadingReferralLink = false;
      })
      .addCase(fetchReferralLink.fulfilled, (state, action) => {
        state.referralLink = action.payload;
        state.loadingReferralLink = false;
      })

      .addCase(sendEmailsInvite.pending, (state) => {
        state.loadingSendingEmailsInvite = true;
        state.emailsSendingError = undefined;
      })
      .addCase(sendEmailsInvite.fulfilled, (state) => {
        state.loadingSendingEmailsInvite = false;
      })
      .addCase(sendEmailsInvite.rejected, (state, action) => {
        state.loadingSendingEmailsInvite = false;
        state.emailsSendingError = action.payload;
      })
      .addCase(fetchMyPreferences.pending, (state) => {
        state.loadingMyPreferences = true;
      })
      .addCase(fetchMyPreferences.rejected, (state, action) => {
        state.myPreferencesError = action.payload;
        state.loadingMyPreferences = false;
      })
      .addCase(fetchMyPreferences.fulfilled, (state, action) => {
        state.myPreferences = convertPreferencesToNotificationsSettings(
          action.payload
        );
        state.myPreferencesError = undefined;
        state.loadingMyPreferences = false;
      })
      .addCase(fetchJourneyRelationships.fulfilled, (state, action) => {
        state.relationshipTypes = action.payload;
      })
      .addCase(updateMyPreferences.fulfilled, (state, action) => {
        state.myPreferences = convertPreferencesToNotificationsSettings(
          action.payload
        );
      })
      .addCase(verifyEmail.fulfilled, (state) => {
        state.isEmailVerificationSuccessful = true;
        state.emailVerficationError = undefined;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isEmailVerificationSuccessful = false;
        state.emailVerficationError = action.payload?.data['error_code'];
      });
  },
});

export const {
  clearMemberInfo,
  clearAuthorInfo,
  updateMemberInfo,
  setAboutYouData,
  clearAboutYouData,
  clearUpdateMemberError,
  updateAddCompanionMemberInfo,
  updateUserVerificationStatus,
  clearMyPreferredTopics,
} = profileSlice.actions;

export default profileSlice.reducer;
